import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import API from '../utils/apiconfig';
import { getApiErrorMessage } from '../utils/formErrors';
import {
  PAYMENT_PROOF_ACCEPT,
  PAYMENT_PROOF_HINT,
  PAYMENT_PROOF_REJECT_MESSAGE,
  isAllowedPaymentProofFile,
  isPaymentProofImage,
} from '../utils/paymentProofFile';
import { formatAppDateTime } from '../utils/dateFormat';

function PaymentRetryUpload() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [context, setContext] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);

  useEffect(() => {
    if (!token) {
      setLoadError('Missing retry token. Use the link from your rejection email.');
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get(API.PAYMENT_RETRY_CONTEXT, { params: { token } })
      .then((r) => {
        if (r.data?.valid === false) {
          setLoadError(r.data.error || 'This retry link is invalid or expired.');
          return;
        }
        setContext(r.data);
      })
      .catch((e) => {
        setLoadError(getApiErrorMessage(e.response?.data, 'Could not load retry details.'));
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const onFileSelect = (file) => {
    if (!file) return;
    if (!isAllowedPaymentProofFile(file)) {
      setUploadError(PAYMENT_PROOF_REJECT_MESSAGE);
      return;
    }
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setSelectedFile(file);
    setUploadError('');
  };

  const onUpload = async () => {
    if (!selectedFile || !token || isUploading) return;
    setIsUploading(true);
    setUploadError('');
    const formData = new FormData();
    formData.append('token', token);
    formData.append('screenshot', selectedFile);
    const txId = context?.resubmit?.defaults?.transaction_id;
    if (txId) formData.append('transaction_id', txId);

    try {
      const response = await api.post(API.UPLOAD_PLAN_SCREENSHOT, formData);
      if (response.data?.success) {
        navigate('/payment/status', { replace: true });
        return;
      }
      setUploadError(getApiErrorMessage(response.data));
    } catch (err) {
      setUploadError(getApiErrorMessage(err.response?.data));
    } finally {
      setIsUploading(false);
    }
  };

  const planName = context?.plan_summary?.plan_name || context?.plan_summary?.display_name || 'Your plan';
  const amount = context?.payment?.amount || context?.resubmit?.defaults?.amount;
  const rejectionReason =
    context?.rejection?.reason || context?.rejection?.message || context?.rejection?.admin_note;

  const isImageFile = isPaymentProofImage(selectedFile);
  const previewUrl =
    isImageFile && selectedFile
      ? (() => {
          if (previewUrlRef.current) return previewUrlRef.current;
          previewUrlRef.current = URL.createObjectURL(selectedFile);
          return previewUrlRef.current;
        })()
      : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-sm text-slate-500">Loading retry details…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-6 text-center">
          <p className="text-4xl mb-3">⚠️</p>
          <h1 className="text-lg font-bold text-slate-900 mb-2">Retry link unavailable</h1>
          <p className="text-sm text-slate-600 mb-4">{loadError}</p>
          <Link
            to="/payments"
            className="inline-block text-sm font-semibold text-indigo-600 hover:text-indigo-800"
          >
            Go to Payments →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[calc(100vh-4rem)] bg-gradient-to-br from-slate-100 via-indigo-50 to-cyan-100 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="rounded-3xl bg-white/90 backdrop-blur border border-white p-6 shadow-lg">
          <p className="text-xs font-bold tracking-widest text-indigo-600 uppercase mb-2">
            Payment re-upload
          </p>
          <h1
            className="text-2xl font-black text-slate-900 mb-2"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            Submit a new payment proof
          </h1>
          <p className="text-sm text-slate-600 mb-4">
            Plan: <strong>{planName}</strong>
            {amount ? <> · Amount: <strong>₹{Number(amount).toLocaleString('en-IN')}</strong></> : null}
          </p>

          {rejectionReason && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-semibold mb-1">Previous rejection reason</p>
              <p>{rejectionReason}</p>
            </div>
          )}

          {context?.retry_expires_at && (
            <p className="text-xs text-slate-500 mb-4">
              Link expires: {formatAppDateTime(context.retry_expires_at)}
            </p>
          )}

          <div
            className="rounded-2xl border-2 border-dashed border-slate-200 h-44 flex flex-col items-center justify-center text-center px-4 mb-4 cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={PAYMENT_PROOF_ACCEPT}
              className="hidden"
              onChange={(e) => onFileSelect(e.target.files?.[0])}
            />
            {selectedFile ? (
              <div>
                <p className="text-sm font-semibold text-slate-800">{selectedFile.name}</p>
                {previewUrl && (
                  <img src={previewUrl} alt="Preview" className="mt-2 max-h-24 mx-auto rounded-lg" />
                )}
              </div>
            ) : (
              <>
                <p className="text-sm font-semibold text-slate-700">Click to choose screenshot or PDF</p>
                <p className="text-xs text-slate-400 mt-1">{PAYMENT_PROOF_HINT}</p>
              </>
            )}
          </div>

          {uploadError && (
            <p className="text-sm text-red-600 mb-3" role="alert">{uploadError}</p>
          )}

          <button
            type="button"
            onClick={onUpload}
            disabled={!selectedFile || isUploading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors"
          >
            {isUploading ? 'Uploading…' : 'Submit new proof'}
          </button>

          <p className="text-xs text-slate-400 text-center mt-4">
            Logged in with the wrong account?{' '}
            <Link to="/login" className="text-indigo-600 hover:underline">Switch user</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default PaymentRetryUpload;
